import { FC } from 'react';

import { Settings } from 'utils/constants/dynamicSettings';

import generateTextBackButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/campaign_design/generate_text_back_button_click';
import generateTextButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/campaign_design/generate_text_button_click';
import { Flex, Link, Loader } from '@hh.ru/magritte-ui';
import { HyperstarSparkleFilledSize16, UturnLeftOutlinedSize16 } from '@hh.ru/magritte-ui/icon';

import useTrls from 'hooks/translation/useTrls';
import useSettings from 'hooks/useSettings';

import { fetchBannerGeneratedText } from 'components/common/BannerTextGeneration/fetchBannerGeneratedText';

import {
    removeBannerGeneratedText,
    selectBannerDefaultText,
    selectBannerGeneratedTexts,
    selectBannerPreviousText,
    selectBannerTextGenerationIsLoading,
    selectBannerTextWasGenerated,
    setBannerDefaultText,
    setBannerPreviousText,
    setBannerTextWasGenerated,
} from 'store/models/bannerTextGeneration';
import { SessionType } from 'store/models/user';
import { useDispatch, useSelector } from 'store/utils';

import styles from 'components/common/BannerTextGeneration/BannerTextGeneration.module.less';

interface BannerTextGenerationProps {
    text: string;
    setBannerText: (text: string) => void;
    vacancyId: string | null;
}

const TrlKeys = {
    generate: 'AdCreate.AdForm.BannerText.generation.generate',
    cancel: 'AdCreate.AdForm.BannerText.generation.cancel',
};

export const BannerTextGeneration: FC<BannerTextGenerationProps> = ({ text, setBannerText, vacancyId }) => {
    const textGenerationIsLoading = useSelector(selectBannerTextGenerationIsLoading);
    const previousText = useSelector(selectBannerPreviousText);
    const defaultText = useSelector(selectBannerDefaultText);
    const generatedTexts = useSelector(selectBannerGeneratedTexts);
    const bannerTextWasGenerated = useSelector(selectBannerTextWasGenerated);
    const isSudo = useSelector((state) => state.user.sessionType === SessionType.Sudo);

    const trls = useTrls();
    const dispatch = useDispatch();
    const settings = useSettings();

    const bannerTextGenerationEnabled = settings(Settings.EnableBannerTextGeneration) as boolean;

    if (!vacancyId || !(bannerTextGenerationEnabled || isSudo)) {
        return null;
    }

    const handleTextGeneration = async () => {
        if (textGenerationIsLoading) {
            return;
        }

        generateTextButtonClick({
            vacancyId,
        });

        if (defaultText === '') {
            dispatch(setBannerDefaultText(text));
        }

        if (!bannerTextWasGenerated) {
            dispatch(setBannerPreviousText(text));
        }
        await dispatch(fetchBannerGeneratedText(vacancyId, setBannerText));
    };

    const handlePreviousTextClick = () => {
        const newGeneratedTexts = generatedTexts.slice(1);
        generateTextBackButtonClick({
            vacancyId,
        });
        dispatch(removeBannerGeneratedText());

        if (newGeneratedTexts.length > 0) {
            setBannerText(newGeneratedTexts[0]);
            dispatch(setBannerTextWasGenerated(true));
        } else {
            setBannerText(previousText);
            dispatch(setBannerTextWasGenerated(false));
        }
    };

    return (
        <div className={styles.container}>
            {!textGenerationIsLoading && generatedTexts.length > 0 && (
                <UturnLeftOutlinedSize16
                    onClick={handlePreviousTextClick}
                    data-qa="banner-text-generation-back-button"
                />
            )}
            <Flex align="center" justify="center" gap={6}>
                {textGenerationIsLoading && <Loader color="negative" size={16} />}
                <Link
                    Element="button"
                    onClick={handleTextGeneration}
                    iconLeft={
                        textGenerationIsLoading ? undefined : <HyperstarSparkleFilledSize16 initialColor="negative" />
                    }
                    typography="label-2-regular"
                    style="neutral"
                    data-qa="banner-text-generation-generate-button"
                >
                    {trls(TrlKeys.generate)}
                </Link>
            </Flex>
        </div>
    );
};
